import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchFinancialAnalytics } from '../../services/analytics/analytics-service';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar
} from 'recharts';

const COLORS = ['#0ea5e9', '#22c55e', '#facc15', '#ef4444', '#a855f7', '#3b82f6', '#f97316'];

const FinancialAnalytics = () => {
    const { t } = useTranslation();
    const [analytics, setAnalytics] = useState(null);

    useEffect(() => {
        fetchFinancialAnalytics().then(setAnalytics).catch(console.error);
    }, []);

    if (!analytics) {
        return <div className="text-center text-gray-600 mt-10">{t('common.loading')}...</div>;
    }

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    return (
        <div className="p-6 space-y-10 max-w-screen-xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">{t('financial.title')}</h1>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="bg-white p-4 rounded-lg shadow text-center">
                    <p className="text-gray-500 text-sm">{t('financial.revenue')}</p>
                    <p className="text-xl font-bold text-blue-600">{formatCurrency(analytics.grossRevenue || 0)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow text-center">
                    <p className="text-gray-500 text-sm">{t('order.totalAmount')} {t('order.discount')}</p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(analytics.totalDiscounts || 0)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow text-center">
                    <p className="text-gray-500 text-sm">Avg {t('order.orderValue')}</p>
                    <p className="text-xl font-bold text-purple-600">{formatCurrency(analytics.averageOrderValue || 0)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow text-center">
                    <p className="text-gray-500 text-sm">Highest {t('order.orderValue')}</p>
                    <p className="text-xl font-bold text-indigo-600">{formatCurrency(analytics.highestOrderValue || 0)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow text-center">
                    <p className="text-gray-500 text-sm">Lowest {t('order.orderValue')}</p>
                    <p className="text-xl font-bold text-gray-600">{formatCurrency(analytics.lowestOrderValue || 0)}</p>
                </div>
            </div>

            {/* Monthly Revenue */}
            <section>
                <h2 className="text-2xl font-semibold mb-4 text-gray-800">{t('dashboard.revenueOverTime')}</h2>
                <div className="w-full h-[350px] bg-white shadow rounded-lg p-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={analytics.monthlyRevenue || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Line type="monotone" dataKey="grossRevenue" stroke="#0ea5e9" name={t('financial.revenue')} strokeWidth={2} />
                            <Line type="monotone" dataKey="totalRevenue" stroke="#22c55e" name={t('financial.totalRevenue')} strokeWidth={2} />
                            <Line type="monotone" dataKey="totalDiscount" stroke="#ef4444" name={t('order.discount')} strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* Top Customers by Revenue */}
            <section>
                <h2 className="text-2xl font-semibold mb-4 text-gray-800">{t('dashboard.topCustomers')}</h2>
                <div className="w-full h-[550px] bg-white shadow rounded-lg p-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.topCustomersByRevenue || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="customerName" angle={-45} textAnchor="end" height={180} interval={0} tick={{ fontSize: 13 }} />
                            <YAxis tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`} />
                            <Tooltip 
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        return (
                                            <div className="bg-white p-3 border rounded shadow-lg">
                                                <p className="font-semibold">{data.customerName}</p>
                                                <p className="text-sm text-gray-600">{t('order.title')}: {data.orderCount}</p>
                                                <p className="text-sm font-bold text-green-600">{t('financial.revenue')}: {formatCurrency(data.totalSpent)}</p>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Legend />
                            <Bar dataKey="totalSpent" fill="#22c55e" name={t('financial.totalRevenue')} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* Top Products by Revenue */}
            <section>
                <h2 className="text-2xl font-semibold mb-4 text-gray-800">{t('product.title')} by {t('financial.revenue')}</h2>
                <div className="w-full h-[600px] bg-white shadow rounded-lg p-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.topProductsByRevenue || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={190} interval={0} tick={{ fontSize: 13 }} />
                            <YAxis tickFormatter={(value) => `€${(value / 1000).toFixed(1)}k`} />
                            <Tooltip 
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        return (
                                            <div className="bg-white p-3 border rounded shadow-lg">
                                                <p className="font-semibold">{data.name}</p>
                                                <p className="text-sm text-gray-600">{t('product.code')}: {data.code}</p>
                                                <p className="text-sm text-gray-600">{t('product.company')}: {data.company}</p>
                                                <p className="text-sm text-gray-600">{t('calculation.quantity')}: {data.quantitySold}</p>
                                                <p className="text-sm font-bold text-green-600">{t('financial.revenue')}: {formatCurrency(data.totalRevenue)}</p>
                                                <p className="text-sm text-blue-600">Avg {t('product.price')}: {formatCurrency(data.averagePrice)}</p>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Legend />
                            <Bar dataKey="totalRevenue" fill="#3b82f6" name={t('financial.totalRevenue')} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* Top Companies by Revenue */}
            <section>
                <h2 className="text-2xl font-semibold mb-4 text-gray-800">{t('product.company')} by {t('financial.revenue')}</h2>
                <div className="w-full h-[550px] bg-white shadow rounded-lg p-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.topCompaniesByRevenue || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="company" angle={-45} textAnchor="end" height={180} interval={0} tick={{ fontSize: 13 }}/>
                            <YAxis tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="revenue" fill="#a855f7" name={t('financial.revenue')} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>
        </div>
    );
};

export default FinancialAnalytics;
